// Utility functions for CSV/TSV and XLSX parsing and data analysis in the browser
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

export type DataFrame = {
  columns: string[];
  index: string[];
  data: Record<string, Record<string, number>>;
};

export function parseCsvToDataFrame(text: string): DataFrame {
  const parsed = Papa.parse(text, { header: true });
  const fields = parsed.meta.fields as string[];
  const columns = fields ? fields.slice(1) : [];
  const index: string[] = [];
  const data: Record<string, Record<string, number>> = {};
  for (const row of parsed.data as any[]) {
    if (!row || !row[fields[0]]) continue;
    const gene = row[fields[0]];
    index.push(gene);
    data[gene] = {};
    for (const col of columns) {
      const val = row[col];
      data[gene][col] = val === '' || val == null ? NaN : Number(val);
    }
  }
  return { columns, index, data };
}

export function parseXlsxToDataFrame(data: ArrayBuffer, sheetName?: string): DataFrame {
  const workbook = XLSX.read(data, { type: 'array' });
  const sheet = workbook.Sheets[sheetName || workbook.SheetNames[0]];
  const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });
  // Convert to DataFrame shape
  // Assume first row is header, first column is gene names
  const [header, ...rows] = json as (string | number)[][];
  const columns = (header as string[]).slice(1);
  const index: string[] = [];
  const dataObj: Record<string, Record<string, number>> = {};
  for (const row of rows) {
    if (!row[0]) continue;
    const gene = String(row[0]);
    index.push(gene);
    dataObj[gene] = {};
    for (let i = 1; i < row.length; ++i) {
      const col = columns[i - 1];
      const val = row[i];
      dataObj[gene][col] = val === '' || val == null ? NaN : Number(val);
    }
  }
  return { columns, index, data: dataObj };
}

export function getTopGenesPerGroup(df: DataFrame, topN = 50) {
  const topGenesPerGroup: Record<string, string[]> = {};
  const geneCounts: Record<string, number> = {};
  const geneGroups: Record<string, string[]> = {};
  for (const group of df.columns) {
    // Sort genes by value (descending), take top N
    const sorted = df.index
      .map(gene => ({ gene, value: df.data[gene][group] }))
      .sort((a, b) => (b.value ?? -Infinity) - (a.value ?? -Infinity))
      .slice(0, topN)
      .map(x => x.gene);
    topGenesPerGroup[group] = sorted;
    for (const gene of sorted) {
      geneCounts[gene] = (geneCounts[gene] || 0) + 1;
      if (!geneGroups[gene]) geneGroups[gene] = [];
      geneGroups[gene].push(group);
    }
  }
  // Sort genes by number of groups they appear in (descending)
  const sortedGenes = Object.entries(geneCounts).sort((a, b) => b[1] - a[1]);
  const sortedGenesWithGroups = sortedGenes.map(([gene, count]) => ({
    gene,
    count,
    groups: geneGroups[gene],
  }));
  return { topGenesPerGroup, geneCounts: sortedGenesWithGroups };
}

export function getTopGenesHeatmap(df: DataFrame, genes: string[], groups: string[]) {
  const result: Record<string, Record<string, number | null>> = {};
  for (const gene of genes) {
    result[gene] = {};
    for (const group of groups) {
      const val = df.data[gene]?.[group];
      result[gene][group] = typeof val === 'number' && !isNaN(val) ? val : null;
    }
  }
  return result;
}

export function getTopGenesForGroupHeatmap(df: DataFrame, group: string, topN = 50) {
  // Get top N genes for the selected group
  const sorted = df.index
    .map(gene => ({ gene, value: df.data[gene][group] }))
    .sort((a, b) => (b.value ?? -Infinity) - (a.value ?? -Infinity))
    .slice(0, topN)
    .map(x => x.gene);
  // Build matrix: rows=top genes, columns=all groups, values=expression
  const matrix: Record<string, Record<string, number | null>> = {};
  for (const gene of sorted) {
    matrix[gene] = {};
    for (const col of df.columns) {
      const val = df.data[gene]?.[col];
      matrix[gene][col] = typeof val === 'number' && !isNaN(val) ? val : null;
    }
  }
  return { genes: sorted, groups: df.columns, matrix };
}

// Compute volcano plot data: log2FC and p-value for each gene between two groups
export function getVolcanoData(df: DataFrame, groupA: string, groupB: string) {
  // For each gene, compute log2FC and p-value (Welch's t-test if >1 sample per group, else just fold change)
  const volcanoData: Array<{ gene: string; log2fc: number; pval: number }> = [];
  for (const gene of df.index) {
    const valA = df.data[gene][groupA];
    const valB = df.data[gene][groupB];
    // If values are arrays (multiple samples per group), compute mean and t-test
    // But in this app, values are likely single numbers per group (mean expression)
    if (typeof valA === 'number' && typeof valB === 'number' && !isNaN(valA) && !isNaN(valB)) {
      // Add a small offset to avoid log(0)
      const offset = 1e-6;
      const log2fc = Math.log2((valA + offset) / (valB + offset));
      // No replicates: use a simple pseudo p-value based on difference magnitude
      // (In real RNA-seq, use t-test or DESeq2, but here just use a placeholder)
      const diff = Math.abs(valA - valB);
      const pval = Math.exp(-diff); // Lower difference = higher pval
      volcanoData.push({ gene, log2fc, pval });
    }
  }
  return volcanoData;
}
